/**
 * Notification Service
 * Zentrale Sammlung aller Benachrichtigungs-bezogenen Funktionen
 * Wiederverwendbar in NotificationBell, NotificationsView, DashboardView
 */

/**
 * Generiert den Link für eine Benachrichtigung basierend auf Typ und Daten
 * @param {Object} notification - Benachrichtigungsobjekt
 * @returns {string|null} Link oder null
 */
export const getNotificationLink = (notification) => {
  const { type, data } = notification
  
  switch (type) {
    case 'receipt_status_changed':
    case 'receipt_cancelled':
    case 'receipt_rejected':
      return data?.receiptId ? `/receipts/${data.receiptId}` : null
    
    case 'team_receipt_created':
      return data?.receiptId ? `/receipts/${data.receiptId}` : null
    
    case 'team_request':
    case 'user_left_team':
      return data?.teamId ? `/teams/${data.teamId}/members` : null
    
    case 'team_request_response':
      return data?.teamId ? `/teams/${data.teamId}` : null
      
    case 'team_field_required':
    case 'team_refund_activated':
      return data?.teamId ? `/teams/${data.teamId}` : null
    
    case 'user_promoted_to_admin':
    case 'user_demoted_from_admin':
      return data?.teamId ? `/teams/${data.teamId}` : null
    
    case 'user_removed_from_team':
      return '/teams' 
    
    case 'team_deleted':
      return '/teams'
    
    case 'newsletter':
      return null 
    
    case 'coin_transfer':
      return data?.teamId ? `/teams/${data.teamId}` : null
    
    default:
      return null
  }
}

/**
 * Formatiert eine Zeitangabe in eine benutzerfreundliche deutsche Zeitspanne
 * @param {string} dateString - ISO-Datumsstring
 * @returns {string} Formatierte Zeitspanne
 */
export const formatTime = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInMinutes = Math.floor((now - date) / (1000 * 60))

  if (diffInMinutes < 1) return 'Gerade eben'
  if (diffInMinutes < 60) return `Vor ${diffInMinutes} Min`

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) return `Vor ${diffInHours} Std`

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return `Vor ${diffInDays} Tag${diffInDays > 1 ? 'en' : ''}`

  return date.toLocaleDateString('de-DE')
}

/**
 * Gibt das FontAwesome Icon für einen Benachrichtigungstyp zurück
 * @param {string} type - Benachrichtigungstyp
 * @returns {string} FontAwesome Icon-Name
 */
export const getNotificationIcon = (type) => {
  const iconMap = {
    'team_receipt_created': 'receipt',
    'receipt_status_changed': 'exchange-alt',
    'receipt_cancelled': 'times-circle',
    'receipt_rejected': 'exclamation-triangle',
    'team_request': 'user-plus',
    'team_request_response': 'user-check',
    'team_field_required': 'exclamation-circle',
    'team_refund_activated': 'money-bill-wave',
    'user_left_team': 'user-minus',
    'user_removed_from_team': 'user-times',
    'user_promoted_to_admin': 'user-crown',
    'user_demoted_from_admin': 'user-minus',
    'team_deleted': 'trash',
    'newsletter': 'envelope',
    'coin_transfer': 'exchange-alt'
  }
  return iconMap[type] || 'bell'
}

/**
 * Gibt die CSS-Klasse für ein Benachrichtigungsicon zurück
 * @param {string} type - Benachrichtigungstyp
 * @returns {string} CSS-Klasse
 */
export const getNotificationIconClass = (type) => {
  const classMap = {
    'team_receipt_created': 'text-success',
    'receipt_status_changed': 'text-info',
    'receipt_cancelled': 'text-warning',
    'receipt_rejected': 'text-danger',
    'team_request': 'text-primary',
    'team_request_response': 'text-success',
    'team_field_required': 'text-warning',
    'team_refund_activated': 'text-success',
    'user_left_team': 'text-info',
    'user_removed_from_team': 'text-danger',
    'user_promoted_to_admin': 'text-success',
    'user_demoted_from_admin': 'text-warning',
    'team_deleted': 'text-danger',
    'newsletter': 'text-secondary',
    'coin_transfer': 'text-info'
  }
  return classMap[type] || 'text-muted'
}

/**
 * Behandelt den Klick auf eine Benachrichtigung
 * @param {Object} notification - Benachrichtigungsobjekt
 * @param {Function} markAsRead - Funktion zum Markieren als gelesen
 * @param {Object} router - Vue Router Instanz
 * @param {Function} onClose - Callback für Dropdown schließen (optional)
 */
export const handleNotificationClick = (notification, markAsRead, router, onClose = null) => {
  // Als gelesen markieren wenn noch nicht gelesen
  if (!notification.readAt) {
    markAsRead(notification.id)
  }

  // Dropdown schließen falls vorhanden
  if (onClose) {
    onClose()
  }

  // Zur entsprechenden Seite navigieren
  const link = getNotificationLink(notification)
  if (link) {
    if (router.currentRoute.value.path === link) {
      window.location.href = link
    } else {
      router.push(link)
    }
  }
}

export default {
  getNotificationLink,
  formatTime,
  getNotificationIcon,
  getNotificationIconClass,
  handleNotificationClick
}
